import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Clock, Image, QrCode, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { sportsApi } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';
import { fmt12 } from '../utils/sportsUtils';
import { fmtDate } from '../utils/dates';
import { QRCodeSVG } from 'qrcode.react';
import { ProofModal } from '../components/ProofPreview';

const STATUS_CONFIG = {
  awaiting_confirmation: { label: 'Pending', color: '#FFB300', bg: '#FFB30018' },
  confirmed:             { label: 'Confirmed', color: '#00E676', bg: '#00E67618' },
  declined:              { label: 'Declined', color: '#FF2D55', bg: '#FF2D5518' },
  cancelled:             { label: 'Cancelled', color: '#737373', bg: '#73737318' },
  hold:                  { label: 'Hold', color: '#A3A3A3', bg: '#A3A3A318' },
  floating:              { label: 'Floating', color: '#888', bg: '#88888818' },
  walk_in:               { label: 'Walk-in', color: '#2979FF', bg: '#2979FF18' },
};

const TABS = [
  { key: 'awaiting_confirmation', label: 'Pending' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'floating', label: 'Floating' },
  { key: null, label: 'All' },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function BookingQRModal({ booking, onClose }) {
  // Must match the real route (/sports/checkin/:bookingId, see SportsApp.js) and stay on the
  // tenant's current host — same pattern as SportsCheckinPage/SportsPublicPage. The previous
  // version built a nonexistent route off the build-time BACKEND host, so scans dead-ended.
  const checkinUrl = `${window.location.origin}/sports/checkin/${booking.id}`;
  const ref = booking.ref_number || booking.id.slice(0, 8).toUpperCase();

  const downloadQR = () => {
    const svg = document.getElementById(`qr-modal-svg-${booking.id}`)?.querySelector('svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 300; canvas.height = 300;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => { ctx.drawImage(img, 0, 0, 300, 300); const a = document.createElement('a'); a.download = `QR-${ref}.png`; a.href = canvas.toDataURL(); a.click(); };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.88)' }}
      onClick={onClose}>
      <div className="rounded-2xl overflow-hidden text-center" style={{ background: '#141414', border: '1px solid #2A2A2A', maxWidth: 340, width: '100%' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid #1E1E1E' }}>
          <p className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>Booking QR Code</p>
          <button onClick={onClose} style={{ color: '#737373', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <div className="px-6 pt-5 pb-2">
          <p className="text-xs mb-1" style={{ color: '#737373' }}>{booking.customer?.name || 'Walk-in'}</p>
          <p className="text-xs mb-1" style={{ color: '#737373' }}>{booking.court_name} · {fmtDate(booking.date)} · {fmt12(booking.start_time)}–{fmt12(booking.end_time)}</p>
          <p className="font-bold mb-4" style={{ fontFamily: 'Oswald, sans-serif', color: '#E63946', letterSpacing: 2, fontSize: 18 }}>{ref}</p>
          <div id={`qr-modal-svg-${booking.id}`} className="flex justify-center mb-4">
            <div style={{ background: '#fff', borderRadius: 12, padding: 12 }}>
              <QRCodeSVG value={checkinUrl} size={180} />
            </div>
          </div>
          <p className="text-[10px] mb-4" style={{ color: '#555' }}>Customer scans this at the court entrance</p>
        </div>
        <div className="px-6 pb-5 flex gap-2">
          <button onClick={downloadQR} data-testid={`download-qr-${booking.id}`}
            className="flex-1 py-2.5 rounded-lg text-sm font-bold uppercase"
            style={{ background: '#E63946', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5 }}>
            Download QR
          </button>
          <a href={checkinUrl} target="_blank" rel="noreferrer"
            className="flex-1 py-2.5 rounded-lg text-sm font-bold uppercase text-center"
            style={{ background: '#1A1A1A', color: '#737373', border: '1px solid #2A2A2A', textDecoration: 'none', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5 }}>
            Open Page
          </a>
        </div>
      </div>
    </div>
  );
}

function BookingCard({ booking, onConfirm, onDecline, onCancel, onViewProof, onViewQR }) {
  const st = STATUS_CONFIG[booking.status] || STATUS_CONFIG.hold;
  const c = booking.customer || {};
  const [declining, setDeclining] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reviewing, setReviewing] = useState(false); // double-confirm the amount before recording
  const [reason, setReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [amountPaid, setAmountPaid] = useState(String(booking.amount || 0));

  const totalAmount = booking.amount || 0;
  const paidNum = parseFloat(amountPaid) || 0;
  const balanceDue = Math.max(totalAmount - paidNum, 0);

  return (
    <div data-testid={`sports-booking-card-${booking.id}`}
      className="rounded-xl p-5 transition-all"
      style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded"
              style={{ background: st.bg, color: st.color }}>{st.label}</span>
            {booking.ref_number && (
              <span className="text-xs font-bold tracking-widest" style={{ color: '#E63946', fontFamily: 'Oswald, sans-serif' }}>{booking.ref_number}</span>
            )}
            {booking.created_at && (
              <span className="text-xs" style={{ color: '#555' }}>Booked on {fmtDate(booking.created_at)}</span>
            )}
          </div>
          <p className="text-base font-bold" style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>{c.name || '—'}</p>
          <p className="text-xs mt-0.5" style={{ color: '#737373' }}>{c.email || ''}{c.email && c.phone ? ' · ' : ''}{c.phone || ''}</p>
        </div>
        <p className="text-xl font-black" style={{ color: '#E63946', fontFamily: 'Oswald, sans-serif', whiteSpace: 'nowrap' }}>
          ₱{(booking.amount || 0).toLocaleString()}
        </p>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: 'Court', value: booking.court_name },
          // The play date — the reservation itself. Accented as the hero so it
          // reads distinctly from the "Booked on" (request) date in the header.
          { label: 'Booked Date', value: fmtDate(booking.date), hero: true },
          { label: 'Time', value: `${fmt12(booking.start_time)} – ${fmt12(booking.end_time)}` },
        ].map(d => (
          <div key={d.label} className="rounded-lg p-3"
            style={{ background: d.hero ? '#E6394610' : '#0A0A0A', border: `1px solid ${d.hero ? '#E6394640' : 'transparent'}` }}>
            <p className="text-xs" style={{ color: d.hero ? '#E63946' : '#555' }}>{d.label}</p>
            <p className="text-sm font-semibold mt-0.5" style={{ color: '#fff' }}>{d.value}</p>
          </div>
        ))}
      </div>

      {/* Proof + actions */}
      <div className="flex items-center gap-2 flex-wrap">
        {booking.proof_key && (
          <button data-testid={`sports-booking-view-proof-${booking.id}`}
            onClick={() => onViewProof(booking.id)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold"
            style={{ background: '#1E1E1E', color: '#A3A3A3' }}>
            <Image className="h-3.5 w-3.5" /> View Proof
          </button>
        )}
        {booking.status === 'awaiting_confirmation' && !declining && !cancelling && !confirming && (
          <>
            <button data-testid={`sports-booking-confirm-${booking.id}`}
              onClick={() => setConfirming(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase transition-colors"
              style={{ background: '#00E67618', color: '#00E676', border: '1px solid #00E67640' }}>
              <CheckCircle className="h-3.5 w-3.5" /> Confirm
            </button>
            <button data-testid={`sports-booking-decline-btn-${booking.id}`}
              onClick={() => setDeclining(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase transition-colors"
              style={{ background: '#FF2D5518', color: '#FF2D55', border: '1px solid #FF2D5540' }}>
              <XCircle className="h-3.5 w-3.5" /> Decline
            </button>
          </>
        )}
        {/* Confirm with amount paid */}
        {confirming && (
          <div className="w-full mt-2 rounded-lg p-3" style={{ background: '#0A1A0A', border: '1px solid #00E67630' }}>
            <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#00E676' }}>Confirm & Record Payment</p>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs" style={{ color: '#737373' }}>Total: ₱{totalAmount.toLocaleString()}</span>
              <span className="text-xs" style={{ color: '#555' }}>|</span>
              <span className="text-xs" style={{ color: '#737373' }}>Amount Received:</span>
              <input
                data-testid={`sports-booking-amount-paid-${booking.id}`}
                type="number" min="0" max={totalAmount} step="1"
                value={amountPaid}
                onChange={e => setAmountPaid(e.target.value)}
                className="w-24 rounded-md px-2 py-1.5 text-sm font-bold"
                style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', outline: 'none' }}
              />
            </div>
            {balanceDue > 0 && (
              <p className="text-xs mb-2" style={{ color: '#FFB300' }}>
                Balance of ₱{balanceDue.toLocaleString()} will be due at the court
              </p>
            )}
            {!reviewing ? (
              <div className="flex gap-2">
                <button onClick={() => setReviewing(true)}
                  data-testid={`sports-booking-confirm-review-${booking.id}`}
                  className="px-4 py-2 rounded-md text-xs font-bold uppercase"
                  style={{ background: '#00E676', color: '#0A0A0A' }}>
                  {balanceDue > 0 ? 'Confirm (Partial)' : 'Confirm (Paid)'}
                </button>
                <button onClick={() => { setConfirming(false); setReviewing(false); setAmountPaid(String(totalAmount)); }}
                  className="px-3 py-2 rounded-md text-xs"
                  style={{ background: '#141414', color: '#737373' }}>
                  Cancel
                </button>
              </div>
            ) : (
              /* Double-confirm — guard against accidentally recording "paid in full" */
              <div className="rounded-md p-3" style={{ background: '#0A0A0A', border: '1px solid #00E67640' }}>
                <p className="text-xs mb-2" style={{ color: '#ddd', lineHeight: 1.5 }}>
                  Record <b style={{ color: '#00E676' }}>₱{paidNum.toLocaleString()}</b> received from <b style={{ color: '#fff' }}>{c.name || 'this customer'}</b>
                  {paidNum >= totalAmount ? ' and mark this booking PAID IN FULL' : `. ₱${balanceDue.toLocaleString()} will remain owed`}.
                  <br />Are you sure they paid this amount?
                </p>
                <div className="flex gap-2">
                  <button onClick={() => { onConfirm(booking.id, 'confirm', paidNum); setConfirming(false); setReviewing(false); }}
                    data-testid={`sports-booking-confirm-submit-${booking.id}`}
                    className="px-4 py-2 rounded-md text-xs font-bold uppercase"
                    style={{ background: '#00E676', color: '#0A0A0A' }}>
                    Yes, record it
                  </button>
                  <button onClick={() => setReviewing(false)}
                    data-testid={`sports-booking-confirm-back-${booking.id}`}
                    className="px-3 py-2 rounded-md text-xs"
                    style={{ background: '#141414', color: '#737373' }}>
                    Back
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {declining && (
          <div className="w-full mt-2 flex gap-2 items-end">
            <input value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Decline reason (optional)"
              className="flex-1 rounded-md px-3 py-2 text-sm"
              style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', outline: 'none' }} />
            <button onClick={() => { onDecline(booking.id, reason); setDeclining(false); }}
              data-testid={`sports-booking-decline-confirm-${booking.id}`}
              className="px-3 py-2 rounded-md text-xs font-bold text-white"
              style={{ background: '#FF2D55' }}>Send</button>
            <button onClick={() => setDeclining(false)} className="px-3 py-2 rounded-md text-xs" style={{ background: '#1E1E1E', color: '#737373' }}>Cancel</button>
          </div>
        )}
        {booking.status === 'confirmed' && (
          <span className="flex items-center gap-1.5 text-xs" style={{ color: '#00E676' }}>
            <CheckCircle className="h-3.5 w-3.5" /> Confirmed {fmtDate(booking.confirmed_at)}
          </span>
        )}
        {booking.status === 'confirmed' && booking.balance_due > 0 && (
          <span className="flex items-center gap-1.5 text-xs px-2 py-0.5 rounded" style={{ background: '#FF2D5514', color: '#FF2D55', border: '1px solid #FF2D5530' }}>
            Balance: ₱{booking.balance_due.toLocaleString()} (Paid: ₱{(booking.amount_paid || 0).toLocaleString()})
          </span>
        )}
        {booking.status === 'confirmed' && (
          <button onClick={() => onViewQR(booking)}
            data-testid={`sports-booking-qr-${booking.id}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold"
            style={{ background: '#2979FF18', color: '#2979FF', border: '1px solid #2979FF30', cursor: 'pointer' }}>
            <QrCode className="h-3.5 w-3.5" /> Show QR
          </button>
        )}
        {/* Cancel button — for confirmed and pending bookings */}
        {(booking.status === 'confirmed' || booking.status === 'awaiting_confirmation') && !cancelling && !declining && !confirming && (
          <button data-testid={`sports-booking-cancel-btn-${booking.id}`}
            onClick={() => setCancelling(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase transition-colors"
            style={{ background: '#73737318', color: '#737373', border: '1px solid #73737340' }}>
            <Ban className="h-3.5 w-3.5" /> Cancel
          </button>
        )}
        {/* Cancel confirmation inline */}
        {cancelling && (
          <div className="w-full mt-2 rounded-lg p-3" style={{ background: '#1A1A1A', border: '1px solid #2A2A2A' }}>
            <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#737373' }}>Cancel this booking?</p>
            <input value={cancelReason} onChange={e => setCancelReason(e.target.value)}
              placeholder="Reason (optional — will not be shown to customer)"
              className="w-full rounded-md px-3 py-2 text-sm mb-2"
              style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', outline: 'none' }} />
            <div className="flex gap-2">
              <button onClick={() => { onCancel(booking.id, cancelReason); setCancelling(false); setCancelReason(''); }}
                data-testid={`sports-booking-cancel-confirm-${booking.id}`}
                className="px-4 py-2 rounded-md text-xs font-bold uppercase"
                style={{ background: '#73737330', color: '#ccc', border: '1px solid #73737350' }}>
                Yes, Cancel Booking
              </button>
              <button onClick={() => { setCancelling(false); setCancelReason(''); }}
                className="px-3 py-2 rounded-md text-xs"
                style={{ background: '#141414', color: '#737373' }}>
                Keep It
              </button>
            </div>
          </div>
        )}
        {booking.status === 'floating' && (
          <>
            <span className="text-xs mr-1" style={{ color: '#888' }}>
              {booking.pay_session_id ? 'Slot released — customer may have paid online' : 'Slot released — customer never paid'}
            </span>
            {booking.pay_session_id && (
              <button data-testid={`sports-booking-reconcile-${booking.id}`}
                onClick={() => onConfirm(booking.id, 'reconcile')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase"
                style={{ background: '#00BCD418', color: '#00BCD4', border: '1px solid #00BCD440' }}
                title="Ask PayMongo whether this booking was actually paid online">
                Check Online Payment
              </button>
            )}
            <button data-testid={`sports-booking-recover-${booking.id}`}
              onClick={() => onConfirm(booking.id, 'recover')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase"
              style={{ background: '#2979FF18', color: '#2979FF', border: '1px solid #2979FF40' }}>
              Recover
            </button>
            <button data-testid={`sports-booking-dismiss-${booking.id}`}
              onClick={() => onDecline(booking.id, 'dismiss')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase"
              style={{ background: '#FF2D5510', color: '#FF2D55', border: '1px solid #FF2D5530' }}>
              Dismiss
            </button>
          </>
        )}
        {booking.booking_type === 'walk_in' && booking.status === 'confirmed' && (
          <span className="text-xs px-2 py-0.5 rounded" style={{ background: '#2979FF18', color: '#2979FF' }}>
            Walk-in {booking.payment_method && `· ${booking.payment_method}`}
          </span>
        )}
        {booking.booking_type === 'competition' && (
          <span className="text-xs px-2 py-0.5 rounded" style={{ background: '#FF6D0018', color: '#FF6D00' }}>
            Competition: {booking.event_name || '—'}
          </span>
        )}
        {booking.status === 'declined' && (
          <span className="flex items-center gap-1.5 text-xs" style={{ color: '#FF2D55' }}>
            <XCircle className="h-3.5 w-3.5" /> Declined{booking.decline_reason ? ` — ${booking.decline_reason}` : ''}
          </span>
        )}
        {booking.status === 'cancelled' && (
          <div className="flex flex-col gap-1 mt-1">
            <span className="flex items-center gap-1.5 text-xs" style={{ color: '#737373' }}>
              <Ban className="h-3.5 w-3.5" /> Cancelled{booking.cancelled_at ? ` on ${fmtDate(booking.cancelled_at)}` : ''}
            </span>
            {booking.cancel_reason && (
              <span className="text-xs pl-5" style={{ color: '#555' }}>Reason: {booking.cancel_reason}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function SportsBookingInquiriesPage() {
  const [activeTab, setActiveTab] = useState('awaiting_confirmation');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proofBookingId, setProofBookingId] = useState(null);
  const [qrBooking, setQrBooking] = useState(null);
  const [busyId, setBusyId] = useState(null);   // guard against double-click on Recover/Reconcile/Dismiss

  const fetchBookings = () => {
    setLoading(true);
    const params = activeTab ? { status: activeTab } : {};
    sportsApi.get('/sports/bookings', { params })
      .then(r => setBookings(r.data || []))
      .catch(() => toast.error('Failed to load bookings'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBookings(); }, [activeTab]);

  const handleConfirm = async (id, action = 'confirm', amountPaidVal = null) => {
    if (busyId === id) return;   // double-click guard
    setBusyId(id);
    try {
      if (action === 'recover') {
        await sportsApi.post(`/sports/bookings/${id}/recover`);
        toast.success('Booking recovered and confirmed!');
      } else if (action === 'reconcile') {
        const r = await sportsApi.post(`/sports/bookings/${id}/reconcile-payment`);
        // Backend uses 200 + {ok:false, code, message} for soft failures so
        // Cloudflare can't hijack the body with an HTML error page.
        if (r.data?.ok === false) {
          toast.error(r.data.message || 'Payment not verified');
        } else if (r.data?.already_confirmed) {
          toast.success('Booking was already paid & confirmed.');
        } else {
          toast.success(`Payment verified with PayMongo — booking confirmed (${r.data?.payment_status || 'paid'})`);
        }
      } else {
        const payload = amountPaidVal !== null ? { amount_paid: amountPaidVal } : {};
        await sportsApi.post(`/sports/bookings/${id}/confirm`, payload);
        toast.success('Booking confirmed! Customer notified.');
      }
      fetchBookings();
    } catch (e) {
      // Structured error (dict with .message) → show its message.
      // HTML body (Cloudflare 5xx page) → show a generic upstream error.
      // Plain string → show as-is.
      let msg;
      const d = e.response?.data;
      const detail = d?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        msg = detail.message;
      } else if (typeof detail === 'string') {
        msg = detail;
      } else if (typeof d === 'string' && d.trim().startsWith('<')) {
        msg = 'The server is temporarily unreachable — please try again in a moment.';
      } else {
        msg = detail || 'Failed';
      }
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const handleDecline = async (id, action) => {
    try {
      if (action === 'dismiss') {
        await sportsApi.post(`/sports/bookings/${id}/dismiss`);
        toast.success('Floating booking dismissed.');
      } else {
        await sportsApi.post(`/sports/bookings/${id}/decline`, { reason: action });
        toast.success('Booking declined. Customer notified.');
      }
      fetchBookings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleCancel = async (id, reason = '') => {
    try {
      await sportsApi.post(`/sports/bookings/${id}/cancel`, { reason });
      toast.success('Booking cancelled.');
      fetchBookings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
  };

  const pendingCount = bookings.filter(b => b.status === 'awaiting_confirmation').length;

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <SportsNavBar pendingCount={pendingCount} />
      <main className="max-w-4xl mx-auto px-5 py-10">
        <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#737373' }}>Manage</p>
        <h1 className="text-4xl uppercase tracking-tight mb-6" style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700 }}>Bookings</h1>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 p-1 rounded-lg" style={{ background: '#141414', display: 'inline-flex' }}>
          {TABS.map(t => (
            <button key={String(t.key)} data-testid={`sports-bookings-tab-${t.label.toLowerCase()}`}
              onClick={() => setActiveTab(t.key)}
              className="px-4 py-2 rounded-md text-sm font-semibold transition-all"
              style={{
                background: activeTab === t.key ? '#E63946' : 'transparent',
                color: activeTab === t.key ? '#fff' : '#737373',
              }}>
              {t.label}
              {t.key === 'awaiting_confirmation' && pendingCount > 0 && ` (${pendingCount})`}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => <div key={i} className="h-44 rounded-xl animate-pulse" style={{ background: '#141414' }} />)}
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-16 rounded-xl" style={{ border: '1px dashed #2A2A2A' }}>
            <Clock className="h-8 w-8 mx-auto mb-3" style={{ color: '#2A2A2A' }} />
            <p className="text-sm" style={{ color: '#737373' }}>
              {activeTab === 'awaiting_confirmation' ? 'No pending bookings.' : 'No bookings found.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map(b => (
              <BookingCard key={b.id} booking={b}
                onConfirm={handleConfirm} onDecline={handleDecline}
                onCancel={handleCancel}
                onViewProof={setProofBookingId}
                onViewQR={setQrBooking} />
            ))}
          </div>
        )}
      </main>
      {proofBookingId && <ProofModal bookingId={proofBookingId} onClose={() => setProofBookingId(null)} />}
      {qrBooking && <BookingQRModal booking={qrBooking} onClose={() => setQrBooking(null)} />}
    </div>
  );
}
